import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useGetLocationData } from "../api/queries";

import { generateQuoteReqDTO } from "../utils";
import { usePermissions } from "./Permissions.context";
import { useLocation, useNavigate, useParams } from "react-router";
import { useCreateNewQuoteReq } from "../api/mutation";
import { useModal } from "./Modal.context";
import { QuoteValidationModal } from "@/components/Modals";
import { GlobeLoaderIcon } from "@/components/icon";
import { geocodeAddress } from "../utils/getAddressDataBySearchQ";
import { message } from "antd";
import { AddressWiaGoogle, MarkersData, QuotesType } from "../types";
import {
  AddressSearchQ,
  PickedAddressType,
  QuoteReqContextType,
  SearchFieldNamesType,
} from "../types/quote";

const QuoteReqContext = createContext<QuoteReqContextType | null>(null);

const generateLocationId = (address: AddressWiaGoogle | undefined) => {
  if (!address) return undefined;
  return `${address.country}, ${address.state}, ${address.city}, ${address.streetName} ${address.streetNumber}, ${address.postalCode}`;
};
const QuoteReqProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const locationState = useLocation().state as
    | { quoteState?: { RFSdate?: string; dueDate?: string } }
    | null;
  const productType = useParams().productType || "DIA";
  const navigate = useNavigate();
  const { openModal } = useModal();
  const [addressSearchQ, setAddressSearQ] = useState<AddressSearchQ>({
    searchQAddressA: undefined,
    searchQAddressB: undefined,
  });
  const { user, company } = usePermissions();
  const [pickedAddress, setPickedAddress] = useState<PickedAddressType>({
    pickedAAddress: undefined,
    pickedBAddress: undefined,
  });
  const [isAddressesTouched, setIsAddressesTouched] = useState<boolean[]>([
    false,
    false,
  ]);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const [pickedMarkers, setPickedMarkers] = useState<MarkersData[]>([]);
  const [nearByMarkers, setNearByMarkers] = useState<{
    near50: MarkersData[];
    near300: MarkersData[];
  } | null>(null);
  const [quoteGeneralData, setQuoteGeneralData] = useState<any>({});
  const { mutate: createNewQuoteReq, isPending } = useCreateNewQuoteReq();

  const { data:nearByAMarkersData } = useGetLocationData(
    pickedAddress.pickedAAddress?.position.lat || 0,
    pickedAddress.pickedAAddress?.position.lng || 0,
    generateLocationId(pickedAddress.pickedAAddress)
  );
  const {data: nearByBMarkersData} = useGetLocationData(
    pickedAddress.pickedBAddress?.position.lat || 0,
    pickedAddress.pickedBAddress?.position.lng || 0,
    generateLocationId(pickedAddress.pickedBAddress)
  )
  const changeSearchQ = (fieldName: SearchFieldNamesType, value: string) => {
    setAddressSearQ((state) => ({ ...state, [fieldName]: value }));
  };
  const addressTouchedHandler = (identifier: string, value: boolean) => {
    if (identifier === "AddressA") {
      return setIsAddressesTouched((state) => [value, state[1]]);
    }
    return setIsAddressesTouched((state) => [state[0], value]);
  };
  const pickAddress = (address: AddressWiaGoogle, index: number) => {
    if (index === 0) {
      return setPickedAddress(
        (state) =>
          ({
            ...state,
            pickedAAddress: address,
          }) as any
      );
    }
    return setPickedAddress(
      (state) =>
        ({
          ...state,
          pickedBAddress: address,
        }) as any
    );
  };
  const generalDataHandler = (fieldName: string, value: any) => {
    setQuoteGeneralData((state: any) => ({ ...state, [fieldName]: value }));
  };
  const checkValidity = async (formData: any, index: number) => {
    try {
      const generateSearchString = Object.entries(formData).reduce(
        (acc: string[], entry: [string, unknown]) => {
          return [...acc, entry[1] as string];
        },
        [] as string[]
      );
      const addressData = await geocodeAddress(
        generateSearchString.slice(0, -2).join(",")
      );
      pickAddress(addressData as unknown as AddressWiaGoogle, index);
    } catch (error: any) {
      message.error(error?.message);
    }
  };
  const addressChangeHandler = (
    identifier: "AddressA" | "AddressB",
    fieldName: string,
    fieldValue: string
  ) => {
    if (identifier === "AddressA") {
      return setPickedAddress((state) => ({
        ...state,
        pickedAAddress: {
          ...state.pickedAAddress,
          [fieldName]: fieldValue,
        } as any,
      }));
    }
    return setPickedAddress((state) => ({
      ...state,
      pickedBAddress: {
        ...state.pickedBAddress,
        [fieldName]: fieldValue,
      } as any,
    }));
  };
  useEffect(() => {
    if (!nearByAMarkersData) return;
    if (!pickedAddress.pickedAAddress) return;
    if (!pickedAddress.pickedAAddress?.position) return;
    const pickedMarkersData: MarkersData[] =
      pickedAddress?.pickedBAddress === undefined
        ? [
            {
              position: pickedAddress.pickedAAddress.position,
              fullAddress: pickedAddress.pickedAAddress.fullAddress,
              providers: nearByAMarkersData,
            },
          ]
        : [
            {
              position: pickedAddress.pickedAAddress.position,
              fullAddress: pickedAddress.pickedAAddress.fullAddress,
              providers: nearByAMarkersData,
            },
            {
              position: pickedAddress.pickedBAddress.position,
              fullAddress: pickedAddress.pickedBAddress.fullAddress,
              providers: nearByBMarkersData,
            },
          ];
    setPickedMarkers(pickedMarkersData);
    const mapMarkers = (data: any[] = []) =>
      data.map((el: any) => ({
        position: { lat: el.building.lat, lng: el.building.long },
        fullAddress: el.building.locationId,
        providers: el.vendors,
      }));
    
    setNearByMarkers(() => {
      const near300 = [
        ...(nearByAMarkersData?.near_300 ? mapMarkers(nearByAMarkersData.near_300) : []),
        ...(nearByBMarkersData?.near_300 ? mapMarkers(nearByBMarkersData.near_300) : []),
      ];
    
      const near50 = [
        ...(nearByAMarkersData?.near_50 ? mapMarkers(nearByAMarkersData.near_50) : []),
        ...(nearByBMarkersData?.near_50 ? mapMarkers(nearByBMarkersData.near_50) : []),
      ];
      return { near300, near50 };
    });
  }, [pickedAddress, nearByAMarkersData, nearByBMarkersData]);

  const removeErrors = (filedName: string) => {
    setValidationErrors((state) => {
      delete state[filedName];
      return state;
    });
  };
  const sendQuote = () => {
    const dateData = {
      RFSdate: locationState?.quoteState?.RFSdate || "",
      dueDate: locationState?.quoteState?.dueDate || "",
    };
    const validatedData = generateQuoteReqDTO(
      productType as QuotesType,
      company.companyType === "customer"
        ? { ...quoteGeneralData, companyId: company.id }
        : quoteGeneralData,
      pickedAddress,
      user?.id || 0,
      dateData,
      isAddressesTouched[0],
      isAddressesTouched[1]
    );
    if (validatedData?.withErrors) {
      return setValidationErrors(validatedData?.data as Record<string, string>);
    }
    createNewQuoteReq(validatedData?.data, {
      onSuccess: (data: { quoteId: string }) => {
        return navigate(`/singleSiteQuoteResults/${data.quoteId}`);
      },
      onError: () => {
        openModal(
          <QuoteValidationModal
            quoteReqData={{
              quoteGeneralData: {
                companyId: quoteGeneralData.companyId,
                endCustomerName: quoteGeneralData.customerName,
                customerProjectNumber: quoteGeneralData.projectName,
              },
              productType,
              pickedAddress,
              date: {
                RFSdate: dateData.RFSdate,
                dueDate: dateData.dueDate,
              },
            }}
          />
        );
      },
    });
  };
  return (
    <QuoteReqContext.Provider
      value={{
        addressSearchQ,
        changeSearchQ,
        pickedAddress,
        pickAddress,
        pickedMarkers,
        nearByMarkers,
        sendQuote,
        validationErrors,
        generalDataHandler,
        removeErrors,
        quoteGeneralData,
        addressChangeHandler,
        addressTouchedHandler,
        checkValidity,
      }}
    >
      {children}
      {isPending && (
        <div className="w-screen h-screen bg-black bg-opacity-40 z-50 absolute top-0 left-0">
          <div className="absolute p-5  top-1/2 left-1/2 max-w-[567px] -translate-x-1/2 -translate-y-1/2 z-50">
            <div className="">
              <GlobeLoaderIcon />
            </div>
          </div>
        </div>
      )}
    </QuoteReqContext.Provider>
  );
};

export const useQuoteReq = (): any => {
  const context = useContext(QuoteReqContext);
  if (!context) {
    throw new Error("useQuoteReq must be used within a QuoteReqProvider");
  }
  return context;
};

export default QuoteReqProvider;
